import { useEffect, useState } from 'react';
import { downloadFileFromDms } from '../services/fileManagement';

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

function resolveDirectImageUrl(fileRef: string, fullPathUrl: string): string {
  if (fullPathUrl && isHttpUrl(fullPathUrl)) return fullPathUrl;
  if (fileRef && isHttpUrl(fileRef)) return fileRef;
  return '';
}

/** DMS storage key from either profile_picture_icon_url or a non-URL fullpath value. */
function resolveDmsFileRef(fileRef: string, fullPathUrl: string): string {
  if (fileRef && !isHttpUrl(fileRef)) return fileRef;
  if (fullPathUrl && !isHttpUrl(fullPathUrl)) return fullPathUrl;
  return '';
}

/** Resolve a DMS file ref (UUID) or full-path URL into a value suitable for `<img src>`. */
export function useDmsImageObjectUrl(
  fileRef: string | undefined | null,
  fullPathUrl: string | undefined | null,
): string {
  const ref = (fileRef ?? '').trim();
  const path = (fullPathUrl ?? '').trim();
  const directUrl = resolveDirectImageUrl(ref, path);
  const dmsRef = resolveDmsFileRef(ref, path);

  const [objectUrl, setObjectUrl] = useState(() => directUrl);

  useEffect(() => {
    if (directUrl) {
      setObjectUrl((prev) => {
        if (prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return directUrl;
      });
      return;
    }

    if (!dmsRef) {
      setObjectUrl((prev) => {
        if (prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
      return;
    }

    let cancelled = false;
    void (async () => {
      const r = await downloadFileFromDms(dmsRef);
      if (cancelled) return;
      if (!r.success) {
        setObjectUrl((prev) => {
          if (prev.startsWith('blob:')) URL.revokeObjectURL(prev);
          return '';
        });
        return;
      }
      const next = URL.createObjectURL(r.data);
      setObjectUrl((prev) => {
        if (prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [directUrl, dmsRef]);

  useEffect(() => {
    return () => {
      if (objectUrl.startsWith('blob:')) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  return objectUrl;
}
